/** Current server authority owns every re-ride decision. */
import { useCallback, useEffect, useRef, useState } from "react";
import type { OfficialTransport } from "./shared";
import { useOnline } from "@/hooks/useOnline";
export interface AttemptOperations {
  repair_until: string | null;
  server_time: string;
  reride: null | {
    original_attempt_number: number;
    replacement_attempt_number: number;
    decision: "pending" | "granted";
    own_vote: boolean | null;
    can_vote: boolean;
    can_overrule: boolean;
    can_cancel: boolean;
  };
}
export function RerideDecision({
  transport,
  attemptPath,
  onSaved,
  disabled = false,
  heading = true,
  onState,
}: {
  transport: OfficialTransport;
  attemptPath: string;
  onSaved: () => Promise<void>;
  disabled?: boolean;
  heading?: boolean;
  onState?: (value: AttemptOperations | null) => void;
}) {
  const online = useOnline();
  const [state, setState] = useState<AttemptOperations | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<"grant" | "deny" | "cancel" | null>(null);
  const generation = useRef(0);
  const alive = useRef(true);
  const contextVersion = useRef(0);
  const statusPath = useRef({ submission: attemptPath, path: attemptPath });
  if (statusPath.current.submission !== attemptPath)
    statusPath.current = { submission: attemptPath, path: attemptPath };
  const load = useCallback(async () => {
    const revision = ++generation.current;
    try {
      const value = await transport.get<AttemptOperations>(`${statusPath.current.path}/operations`);
      if (alive.current && revision === generation.current) {
        setState(value);
        onState?.(value);
        if (value.reride)
          statusPath.current.path = attemptPath.replace(
            /\/attempts\/\d+$/,
            `/attempts/${value.reride.original_attempt_number}`,
          );
        setError("");
      }
    } catch (cause) {
      if (alive.current && revision === generation.current) {
        setState(null);
        onState?.(null);
      }
      throw cause;
    }
  }, [transport, attemptPath, onState]);
  useEffect(() => {
    alive.current = true;
    let active = true;
    const context = contextVersion;
    const requests = generation;
    setState(null);
    setConfirm(null);
    setBusy(false);
    const refresh = () =>
      void load().catch((cause: unknown) => {
        if (active)
          setError(cause instanceof Error ? cause.message : "Could not load re-ride decision");
      });
    if (online) refresh();
    const timer = window.setInterval(() => {
      if (navigator.onLine) refresh();
    }, 5000);
    return () => {
      active = false;
      alive.current = false;
      context.current++;
      requests.current++;
      clearInterval(timer);
    };
  }, [load, online]);
  async function save(grant: boolean, overrule = false, cancel = false) {
    const rr = state?.reride;
    if (!online || busy || disabled || !rr) return;
    const revision = contextVersion.current;
    const current = () => alive.current && contextVersion.current === revision;
    const originalPath = attemptPath.replace(
      /\/attempts\/\d+$/,
      `/attempts/${rr.original_attempt_number}`,
    );
    setBusy(true);
    setError("");
    try {
      await transport.post(
        `${originalPath}/reride/${cancel ? "cancel" : "vote"}`,
        cancel ? {} : { grant, ...(overrule ? { overrule: true } : {}) },
        { canSend: current, canRetry: () => false },
      );
      if (!current()) return;
      setConfirm(null);
      await load();
      await onSaved();
    } catch (cause) {
      if (current()) setError(cause instanceof Error ? cause.message : "Could not save decision");
    } finally {
      if (current()) setBusy(false);
    }
  }
  const rr = state?.reride;
  const blocked = !online || busy || disabled;
  return (
    <section aria-label="Re-ride decision" className="official-stack">
      {heading && <h3>Re-ride decision</h3>}
      {!online && <p role="status">Reconnect to review and decide a re-ride.</p>}
      {error && (
        <p role="alert">
          {error}{" "}
          <button onClick={() => void load().catch(() => undefined)} disabled={blocked}>
            Reload decision
          </button>
        </p>
      )}
      {!state && online && !error && <p role="status">Loading decision…</p>}
      {state && !rr && <p>No active re-ride.</p>}
      {rr && (
        <>
          <p>
            Original attempt {rr.original_attempt_number} → Replacement attempt{" "}
            {rr.replacement_attempt_number}
          </p>
          <p>Panel decision: {rr.decision}</p>
          {rr.own_vote !== null && <p>Your vote: {rr.own_vote ? "Grant" : "Deny"}</p>}
          {rr.can_vote && !confirm && (
            <div>
              <button disabled={blocked} onClick={() => void save(true)}>
                Vote grant
              </button>{" "}
              <button disabled={blocked} onClick={() => void save(false)}>
                Vote deny
              </button>
            </div>
          )}
          {rr.can_overrule && !confirm && (
            <div>
              <button disabled={blocked} onClick={() => setConfirm("grant")}>
                Approve re-ride by overrule…
              </button>{" "}
              <button disabled={blocked} onClick={() => setConfirm("deny")}>
                Deny re-ride by overrule…
              </button>
            </div>
          )}
          {rr.can_cancel && !confirm && (
            <button disabled={blocked} onClick={() => setConfirm("cancel")}>
              Cancel re-ride…
            </button>
          )}
          {confirm && (
            <fieldset disabled={blocked}>
              <legend>
                {confirm === "cancel"
                  ? "Cancel re-ride?"
                  : `${confirm === "grant" ? "Approve" : "Deny"} by official overrule?`}
              </legend>
              <p>
                {confirm === "cancel"
                  ? "Restore the original attempt. Replacement scores remain in the audit; an unscored replacement is removed."
                  : confirm === "deny"
                    ? "Override the panel and restore the original attempt. The configured denied-request fall rule applies."
                    : "Override the panel vote and allow the replacement attempt to be scored."}
              </p>
              <button
                onClick={() =>
                  void save(confirm === "grant", confirm !== "cancel", confirm === "cancel")
                }
              >
                Confirm {confirm === "cancel" ? "cancellation" : "overrule"}
              </button>{" "}
              <button onClick={() => setConfirm(null)}>Keep current decision</button>
            </fieldset>
          )}
        </>
      )}
    </section>
  );
}
